// TODAY (home) surface — the "Signal" re-layout (Today Screen v2): the voice-face day greeting in the
// sticky toolbar with the LIVE marker, the KPI strip (one mini-card per Metric on the shared wire), then
// two cards — the amber approval-queue banner (routes to the Approvals inbox) above the bounded Ready
// gate preview and the "In motion" open-Task list, and the "What Ritona did" card (schedule
// health + the activity log on demand). Cross-surface clicks navigate via the deep-linkable hash (F10);
// a motion row opens the Task-detail modal.
//
// Thin over services: the surface renders the composed TodayProjection and routes a gate exercise to the
// owning module's Gate via the Stage-1 POST route (exerciseGate) — it sets NO approval field (P-GATE). After
// a routed exercise it re-reads the projection so it shows the state the owning module recorded (G-182).
// The greeting's weekday/time-of-day comes from the local clock and the shell identity (display only);
// every figure derives from the projection (G-182) — nothing is hand-kept, nothing fabricated (P-SELF; the
// design's "What Ritona learned" memory card has no Today-projection counterpart, so it does not render).

import { useCallback, useEffect } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { GateList } from './components/GateList.tsx';
import { TodayKpiStrip } from './components/TodayKpiStrip.tsx';
import { WaitingBanner, MotionList, RitonaLog } from './components/TodayCards.tsx';
import { flattenBoard } from './components/metric-wire.ts';
import { ScreenToolbar, ScreenBody, TbLive, TbInfo } from '../app/screen-chrome.tsx';
import { useViewerIdentity } from '../app/viewer-identity.tsx';
import { useShell } from '../app/ShellContext.tsx';
import type { TodayProjection, ApprovalGateKind } from './projections.ts';
import type { RouteResult } from './api-client.ts';
import { subscribeToTaskProjectionChanges } from './task-projection-events.ts';
import { createApprovalTriage } from './approval-triage.ts';

/** The day greeting (prototype TODAY.greeting, Signal voice): weekday + time-of-day + first name —
 *  the local clock and the shell identity, display only. */
function greeting(viewerName: string | undefined): string {
  const now = new Date();
  const weekday = now.toLocaleDateString('en-GB', { weekday: 'long' });
  const hour = now.getHours();
  const daypart = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const first = viewerName?.trim().split(/\s+/u)[0];
  return first === undefined || first === ''
    ? `${weekday} ${daypart}`
    : `${weekday} ${daypart}, ${first}`;
}

/** In-app navigation to another surface via the deep-linkable hash (F10 — the shell re-derives). */
function goToSurface(id: 'metrics' | 'approvals' | 'tasks'): void {
  window.location.hash = id;
}

/** The Today aggregate surface. */
export function TodaySurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const shell = useShell();
  const viewer = useViewerIdentity();
  const state = useProjection<TodayProjection>(api.getToday);
  const reload = state.reload;
  useEffect(() => subscribeToTaskProjectionChanges(reload), [reload]);

  // Route a gate exercise to the owning module's Gate, then re-read so the surface shows the recorded state.
  const onExercise = useCallback(
    async (kind: ApprovalGateKind, objectId: string, expectedRevision: number | null) => {
      const result = await api.exerciseGate(kind, objectId, expectedRevision);
      if (result.ok) reload();
      return result;
    },
    [api, reload],
  );

  return (
    <>
      <ScreenToolbar title={greeting(viewer?.name)} voice>
        {state.data !== null ? <TbLive /> : null}
        <TbInfo
          title="Today"
          tip="A live read of everything in motion. Nothing here is hand-kept — and nothing you started is forgotten."
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(today) => (
            <TodayBody today={today} onExercise={onExercise} onOpenTask={shell.openTaskModal} />
          )}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}

/** The loaded Today body — the KPI strip, then the decision card + the "What Ritona did" card. */
function TodayBody({
  today,
  onExercise,
  onOpenTask,
}: {
  readonly today: TodayProjection;
  readonly onExercise: (
    kind: ApprovalGateKind,
    objectId: string,
    expectedRevision: number | null,
  ) => Promise<RouteResult>;
  readonly onOpenTask: (id: string) => void;
}): React.JSX.Element {
  const approvalTriage = createApprovalTriage(today.gates);
  return (
    <>
      <TodayKpiStrip
        metrics={flattenBoard({ metrics: today.metrics }).metrics}
        onOpenKpis={() => goToSurface('metrics')}
      />
      <div className="today-cards">
        <div className="tc-card">
          <WaitingBanner
            summary={approvalTriage.summary}
            onOpenApprovals={() => goToSurface('approvals')}
          />
          <section aria-label="Needs you" className="tc-gates">
            <GateList
              variant="gate"
              gates={approvalTriage.todayReady}
              emptyText="Nothing is ready for you right now."
              onExercise={onExercise}
            />
          </section>
          <MotionList
            tasks={today.openTasks}
            workload={today.workload}
            onOpenTask={onOpenTask}
            onOpenBoard={() => goToSurface('tasks')}
          />
        </div>
        <div className="tc-card">
          <RitonaLog health={today.scheduleHealth} activity={today.activity} />
        </div>
      </div>
    </>
  );
}
